import type { CartItem } from '../../services/cartService';
import { quebecTaxFromPreTax, taxeHebergementAmountForNights } from '../../lib/quebecSalesTax';

export type CheckoutShoppingCartSlice = {
  startDate?: Date | null;
  endDate?: Date | null;
  groups?: Array<{ id: string; adults: number; kids: number }>;
};

export type VumPromoSlice = {
  nights: number;
  maxPersons: number;
  minPersons?: number;
  /** Max adults allowed (when option distinguishes adults from children) */
  maxAdults?: number;
  /** Max children allowed (when option distinguishes adults from children) */
  maxChildren?: number;
  /** Forfait price paid to VUM — used as additional tax base (GST, QST, taxe d'hébergement) */
  forfaitPrice?: number;
};

export type CheckoutTotals = {
  subtotal: number;
  /** Accommodation-only subtotal, used as base for taxe d'hébergement */
  accommodationSubtotal: number;
  /**
   * VUM forfait price used as additional tax base.
   * The forfait itself is already paid to VUM; only the taxes are collected by Pignon Rouge.
   */
  vumForfaitTaxBase: number;
  gst: number;
  qst: number;
  /** Taxe d'hébergement ($3.50/night) on enabled accommodation nights */
  taxeHebergement: number;
  total: number;
  /** 30% deposit — charged upfront for partial payment plan */
  depositAmount: number;
  /** 70% balance — owed at arrival for partial payment plan */
  balanceAmount: number;
  duration: number;
  totalGuests: number;
  totalAdults: number;
  totalChildren: number;
  /** Amount saved thanks to VUM promo (included nights) */
  vumDiscount: number;
  /** Extra nights beyond VUM included nights */
  vumExtraNights: number;
  /** Nights covered by VUM promo */
  vumIncludedNights: number;
  /** True when total guests exceed VUM max persons */
  vumCapacityExceeded: boolean;
  /** True when adults count exceeds VUM maxAdults */
  vumAdultsExceeded: boolean;
  /** True when children count exceeds VUM maxChildren */
  vumChildrenExceeded: boolean;
  /** Any VUM capacity constraint is violated */
  vumAnyCapacityExceeded: boolean;
};

const DEPOSIT_RATE = 0.30;

export function computeCheckoutTotals(
  selectedItems: CartItem[],
  shoppingCartData?: CheckoutShoppingCartSlice,
  vumPromo?: VumPromoSlice | null,
): CheckoutTotals {
  let subtotal = 0;
  let duration = 1;
  let vumDiscount = 0;
  let vumExtraNights = 0;
  let vumIncludedNights = 0;

  if (shoppingCartData?.startDate && shoppingCartData?.endDate) {
    const start = shoppingCartData.startDate;
    const end = shoppingCartData.endDate;
    const diffTime = end.getTime() - start.getTime();
    duration = Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)));
  }

  const totalAdults = shoppingCartData?.groups?.reduce((sum, g) => sum + g.adults, 0) ?? 0;
  const totalChildren = shoppingCartData?.groups?.reduce((sum, g) => sum + g.kids, 0) ?? 0;
  const totalGuests = (totalAdults + totalChildren) || 1;

  const vumCapacityExceeded = !!vumPromo && totalGuests > vumPromo.maxPersons;
  const vumAdultsExceeded = !!vumPromo && vumPromo.maxAdults !== undefined && totalAdults > vumPromo.maxAdults;
  const vumChildrenExceeded = !!vumPromo && vumPromo.maxChildren !== undefined && totalChildren > vumPromo.maxChildren;
  const vumAnyCapacityExceeded = vumCapacityExceeded || vumAdultsExceeded || vumChildrenExceeded;

  if (vumPromo) {
    vumIncludedNights = Math.min(duration, vumPromo.nights);
    vumExtraNights = Math.max(0, duration - vumPromo.nights);
  }

  // VUM forfait covers only ONE accommodation — the cheapest one.
  // If there are multiple accommodations, the discount applies to the lowest-priced one
  // and the others are charged at full price.
  const accommodations = selectedItems.filter((i) => i.type === 'accommodation');
  const cheapestAccommodationId = vumPromo && accommodations.length > 0
    ? accommodations.reduce((min, i) => i.itemPrice < min.itemPrice ? i : min).id
    : null;

  let accommodationSubtotal = 0;
  /** Nights on accommodation lines subject to taxe d'hébergement (fixed $3.50/night each) */
  let lodgingTaxNightCount = 0;

  selectedItems.forEach((item) => {
    const qty = item.quantity ?? 1;
    if (item.type === 'accommodation') {
      let accCost = 0;
      if (vumPromo && item.id === cheapestAccommodationId) {
        // Cheapest accommodation: VUM discount applies (included nights are $0)
        accCost = item.itemPrice * vumExtraNights;
        vumDiscount += item.itemPrice * vumIncludedNights;
      } else {
        // No promo OR additional accommodations beyond the forfait: full price
        accCost = item.itemPrice * duration;
      }
      subtotal += accCost;
      accommodationSubtotal += accCost;
      if (item.taxeHebergementEnabled !== false) {
        lodgingTaxNightCount += duration;
      }
    } else if (item.type === 'activity') {
      if (item.itemId.startsWith('vum-included-') && item.vumIncludedQty !== undefined) {
        // Free units covered by the VUM promo; extra units above included qty are charged
        const extraQty = Math.max(0, qty - item.vumIncludedQty);
        subtotal += extraQty * (item.vumLinkedActivityPrice ?? 0) * totalGuests;
      } else {
        subtotal += item.itemPrice * totalGuests * qty;
      }
    } else {
      subtotal += item.itemPrice * qty;
    }
  });

  // The VUM forfait price is already paid to promovalidation.com.
  // Pignon Rouge must still collect GST, QST and taxe d'hébergement on that value.
  let vumForfaitTaxBase = vumPromo?.forfaitPrice ?? 0;

  // Full taxable amount = extra-nights/activities subtotal + VUM forfait price
  const taxableSubtotal = subtotal + vumForfaitTaxBase;

  const taxeHebergement = taxeHebergementAmountForNights(lodgingTaxNightCount, lodgingTaxNightCount > 0);
  // TPS/TVQ on (cart pre-tax subtotal + taxe d'hébergement), same stack as `quebecStayNightPricing`.
  const gstQstBase = Math.max(0, taxableSubtotal + taxeHebergement);
  const qMain = quebecTaxFromPreTax(gstQstBase);
  const gst = qMain.tps;
  const qst = qMain.tvq;
  const total = qMain.total;

  const depositAmount = Math.round(total * DEPOSIT_RATE * 100) / 100;
  const balanceAmount = Math.round((total - depositAmount) * 100) / 100;

  return {
    subtotal,
    accommodationSubtotal,
    vumForfaitTaxBase,
    gst,
    qst,
    taxeHebergement,
    total,
    depositAmount,
    balanceAmount,
    duration,
    totalGuests,
    totalAdults,
    totalChildren,
    vumDiscount,
    vumExtraNights,
    vumIncludedNights,
    vumCapacityExceeded,
    vumAdultsExceeded,
    vumChildrenExceeded,
    vumAnyCapacityExceeded,
  };
}

export type CartSelectionTotals = CheckoutTotals & { itemCount: number };

export function computeCartSelectionTotals(
  selectedLineItems: CartItem[],
  shoppingCartData?: CheckoutShoppingCartSlice,
  vumPromo?: VumPromoSlice | null,
): CartSelectionTotals {
  const base = computeCheckoutTotals(selectedLineItems, shoppingCartData, vumPromo);
  return { ...base, itemCount: selectedLineItems.length };
}
